import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ModernLogo } from "@/components/ModernLogo";
import { PaymentCalculator } from "@/components/PaymentCalculator";
import { 
  UserPlus,
  Shield,
  Search,
  MessageSquare,
  Calendar,
  MapPin,
  CreditCard,
  Star,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Clock,
  Award,
  Phone,
  Mail,
  FileCheck,
  Smartphone,
  BellRing
} from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";

export function HowItWorks() {
  const steps = [
    {
      step: 1,
      title: "Sign Up & Create Profile",
      description: "Create your professional profile in under 10 minutes",
      icon: <UserPlus className="w-8 h-8 text-primary" />,
      details: [
        "Complete registration with basic details",
        "Upload insurance certificate and qualifications", 
        "Add your trade specialties and service areas",
        "Set your availability and preferred job types"
      ],
      timeRequired: "10 minutes",
      difficulty: "Easy"
    },
    {
      step: 2,
      title: "Get Verified",
      description: "Quick verification process to build customer trust",
      icon: <Shield className="w-8 h-8 text-primary" />,
      details: [
        "Upload valid insurance documentation",
        "Verify your professional qualifications",
        "Complete insurance verification if required",
        "Get your verified badge to attract customers"
      ],
      timeRequired: "24-48 hours",
      difficulty: "Easy"
    },
    {
      step: 3,
      title: "Browse Available Jobs",
      description: "View local jobs that match your skills instantly",
      icon: <Search className="w-8 h-8 text-primary" />,
      details: [
        "Filter jobs by location, trade, and urgency",
        "View detailed job descriptions and requirements", 
        "See customer ratings and job budgets",
        "Apply to jobs that interest you"
      ],
      timeRequired: "Ongoing",
      difficulty: "Easy"
    },
    {
      step: 4,
      title: "Respond to Jobs",
      description: "Send quotes and communicate with customers",
      icon: <MessageSquare className="w-8 h-8 text-primary" />,
      details: [
        "Submit competitive quotes quickly",
        "Message customers directly through the platform",
        "Share photos of previous similar work",
        "Answer customer questions promptly"
      ],
      timeRequired: "Quickly",
      difficulty: "Medium"
    },
    {
      step: 5,
      title: "Schedule & Complete Work",
      description: "Arrange convenient times and deliver quality service",
      icon: <Calendar className="w-8 h-8 text-primary" />,
      details: [
        "Coordinate schedules with customers",
        "Confirm job details and requirements",
        "Complete work to professional standards",
        "Take photos for completion verification"
      ],
      timeRequired: "As scheduled",
      difficulty: "Medium"
    },
    {
      step: 6,
      title: "Get Paid",
      description: "Receive 93% of your quote directly from customer",
      icon: <CreditCard className="w-8 h-8 text-primary" />,
      details: [
        "Customer pays 10% upfront to platform (3% booking + 7% deposit)",
        "You receive 93% of your quoted amount directly from customer", 
        "Platform keeps the 10% upfront payment as revenue",
        "No waiting - get paid immediately upon job completion"
      ],
      timeRequired: "Upon completion",
      difficulty: "Easy"
    }
  ];

  const features = [
    {
      icon: <BellRing className="w-6 h-6 text-primary" />,
      title: "Instant Job Alerts",
      description: "Get notified immediately when new jobs match your criteria via SMS, email, or app notifications."
    },
    {
      icon: <MapPin className="w-6 h-6 text-primary" />,
      title: "Location-Based Matching",
      description: "Only see jobs in your service area. Set multiple locations and travel radius preferences."
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-primary" />,
      title: "Direct Communication",
      description: "Chat directly with customers through our secure messaging system. Share photos and updates."
    },
    {
      icon: <FileCheck className="w-6 h-6 text-primary" />,
      title: "Job Management",
      description: "Track all your jobs from application to completion. Upload progress photos and updates."
    },
    {
      icon: <Star className="w-6 h-6 text-primary" />,
      title: "Review System",
      description: "Build your reputation with customer reviews and ratings. Showcase your best work."
    },
    {
      icon: <Smartphone className="w-6 h-6 text-primary" />,
      title: "Mobile App",
      description: "Manage your business on the go with our mobile app. Available for iOS and Android."
    }
  ];

  const tips = [
    {
      title: "Response Time Matters",
      description: "Customers expect fast responses. Quick responses increase your chances of winning jobs.",
      icon: <Clock className="w-5 h-5 text-primary" />
    },
    {
      title: "Professional Communication",
      description: "Clear, polite communication builds trust. Always respond professionally to customer inquiries.",
      icon: <MessageSquare className="w-5 h-5 text-primary" />
    },
    {
      title: "Competitive Pricing",
      description: "Research local rates and price competitively. Include all costs in your initial quote.",
      icon: <CreditCard className="w-5 h-5 text-primary" />
    },
    {
      title: "Quality Documentation",
      description: "Upload clear photos of your work and maintain proper insurance documentation.",
      icon: <FileCheck className="w-5 h-5 text-primary" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="How Sortable Works - Find Local Tradesmen in 6 Steps"
        description="Discover how easy it is to find and hire trusted local tradesmen with Sortable. Post your job, get quotes, and hire verified pros in 6 simple steps."
        keywords="how sortable works, find tradesmen, hire contractors, get quotes, home services process, step by step guide, job posting process, contractor matching, quote comparison, hiring process, service booking, tradesman selection, project management, customer journey, user guide, platform tutorial, service request, instant quotes, verified professionals, insurance verification, secure payments, job tracking, completion guarantee, customer support, dispute resolution, quality assurance, rating system, review process, communication tools, project timeline, cost estimation, service categories, local search, area coverage, availability checking, emergency services, same day service, weekend availability, out of hours support, professional tradesmen guide, contractor hiring tips, home improvement process, repair service booking, maintenance scheduling, emergency call out, urgent repairs, property maintenance workflow, skilled trades directory"
        canonical="https://sortable.uk/how-it-works"
        ogImage="https://sortable.uk/hero-tradesman.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Find and Hire Tradesmen on Sortable",
          "description": "Step-by-step guide to finding and hiring verified tradesmen",
          "step": [
            {
              "@type": "HowToStep",
              "name": "Sign Up & Create Profile",
              "text": "Create your professional profile in under 10 minutes"
            },
            {
              "@type": "HowToStep", 
              "name": "Get Verified",
              "text": "Quick verification process to build customer trust"
            },
            {
              "@type": "HowToStep",
              "name": "Browse Available Jobs", 
              "text": "View local jobs that match your skills instantly"
            }
          ]
        }}
      />
      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/why-us">
                <Button variant="ghost" size="sm">
                  <Shield className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                  <span className="hidden md:inline">Why Us</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                  <span className="hidden md:inline">Back to For Tradesmen</span>
                  <span className="md:hidden sr-only">Back</span>
                </Button>
              </Link>
              <Link to="/how-it-works-customer">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Customers</span>
                  <span className="md:hidden">Customers</span>
                </Button>
              </Link>
              <Link to="/">
                <Button variant="default" size="sm">
                  <span className="md:hidden">Start</span>
                  <span className="hidden md:inline">Get Started</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-hero/10 border border-primary/20 text-primary px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Award className="h-5 w-5" />
            How It Works for Tradesmen
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            From Sign-Up to Payment in 6 Simple Steps
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Learn exactly how our platform works for tradesmen. From creating your profile 
            to getting paid, we'll guide you through every step of the process.
          </p>
          <Link to="/why-us">
            <Button variant="outline" size="lg" className="shadow-sm hover:shadow-md">
              <Shield className="h-4 w-4 mr-2" />
              See Our Payment & Protection Guarantees
            </Button>
          </Link>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="space-y-16">
            {steps.map((step, index) => (
              <div key={index} className={`flex flex-col lg:flex-row gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className="flex-1">
                  <Card className="border-border/50 shadow-elegant hover:shadow-glow transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center">
                          {step.icon}
                        </div>
                        <div>
                          <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mb-2">
                            {step.step}
                          </div>
                          <CardTitle className="text-2xl">{step.title}</CardTitle>
                        </div>
                      </div>
                      <CardDescription className="text-lg">{step.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 mb-6">
                        {step.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{detail}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-2 text-primary">
                          <Clock className="w-4 h-4" />
                          <span>{step.timeRequired}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Shield className="w-4 h-4" />
                          <span>{step.difficulty}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className="flex-shrink-0">
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block">
                      <ArrowRight className="w-8 h-8 text-primary/50" />
                    </div>
                  )}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden">
                      <div className="w-px h-8 bg-primary/50 mx-auto"></div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Our straightforward pricing structure means no surprises for you or your customers.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-border/50 shadow-elegant">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">How Pricing Works</CardTitle>
                <CardDescription className="text-lg">
                  Fair and transparent for everyone
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-center">Customer Payment</h3>
                    <div className="bg-primary/5 p-6 rounded-lg border border-primary/20">
                      <div className="text-center space-y-2">
                        <div className="text-xl font-bold text-primary">10% Upfront</div>
                        <div className="text-sm text-muted-foreground">To Platform (3% booking + 7% deposit)</div>
                        <div className="text-lg font-semibold">+</div>
                        <div className="text-2xl font-bold">93% Direct</div>
                        <div className="text-sm text-muted-foreground">To You Upon Completion</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-center">You Receive</h3>
                    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                      <div className="text-center space-y-2">
                        <div className="text-3xl font-bold text-green-700">93%</div>
                        <div className="text-sm text-muted-foreground">Of Your Quote</div>
                        <div className="text-lg font-semibold">Direct from Customer</div>
                        <div className="text-sm text-muted-foreground">No platform deduction</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                  <div className="text-center space-y-2">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto" />
                    <p className="text-sm text-muted-foreground mb-4">
                      <strong>Example:</strong> You quote £500 → Customer pays £50 upfront to platform<br />
                      Then pays you: <strong>£465 (93%)</strong> directly upon job completion
                    </p>
                  </div>

                  <div className="mt-6">
                    <PaymentCalculator userType="tradesman" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Platform Features That Help You Succeed
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to manage and grow your tradesman business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-border/50 text-center hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Tips */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Tips for Success
            </h2>
            <p className="text-lg text-muted-foreground">
              Learn from our most successful tradesmen with these proven strategies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tips.map((tip, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                      {tip.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{tip.title}</h3>
                      <p className="text-muted-foreground">{tip.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Common Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "How quickly can I start receiving jobs?",
                answer: "Once your profile is verified (24-48 hours), you can immediately start browsing and applying for jobs in your area."
              },
              {
                question: "Do I need to respond to every job posting?", 
                answer: "No, you can browse all available jobs and only respond to those that interest you and match your skills."
              },
              {
                question: "What happens if a customer isn't satisfied?",
                answer: "We have a dispute resolution process. Payment is held until both parties agree the work is complete to standard."
              },
              {
                question: "Can I set my own prices?",
                answer: "Yes, you quote your own prices for jobs. We recommend researching local rates to stay competitive."
              },
              {
                question: "Is there a limit to how many jobs I can take?",
                answer: "No limits. Take as many or as few jobs as you want based on your availability and capacity."
              }
            ].map((faq, index) => (
              <Card key={index} className="border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 bg-gradient-hero">
        <div className="container mx-auto text-center text-primary-foreground">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of successful tradesmen who have transformed their businesses with our simple 6-step process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
                Start Step 1: Sign Up Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-primary bg-white hover:bg-white/90" asChild>
              <a href="tel:08000438880">
                <Phone className="mr-2 h-5 w-5" />
                Get Help: Call Us
              </a>
            </Button>
          </div>
          <p className="text-sm mt-4 opacity-75">
            Setup takes less than 10 minutes • Start earning within 48 hours
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t border-border/50 py-12 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground">
                The UK's most trusted platform connecting customers with verified, professional tradesmen.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/about" className="hover:text-primary">About Us</Link></li>
                <li><Link to="/success-stories" className="hover:text-primary">Success Stories</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Tradesmen</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join as Tradesman</Link></li>
                <li><Link to="/pricing" className="hover:text-primary">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>08000438880</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>realise@post.com</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-border/50 mt-8 pt-8 text-center text-muted-foreground">
            <p>&copy; 2025 Sortable. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}